import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { useMemo } from "react";

import type { InferTransformedProcedureOutput } from "@goose/api";
import type { ContactId, OrganizationId } from "@goose/schema/Salesforce";

import { DataTable } from "~/components/DataTable";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { Spinner } from "~/components/ui/Spinner";
import { formatDate } from "~/lib/format";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute(
  "/_authenticated/admin/organizations/$organizationId/contacts/$contactId/",
)({
  component: ContactIndex,
});

function ContactIndex() {
  const trpc = useTRPC();
  const { organizationId, contactId } = Route.useParams();

  const opportunities = useQuery(
    trpc.opportunities.listByContact.queryOptions({
      organizationId,
      contactId,
    }),
  );

  if (opportunities.isLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  if (opportunities.isError) {
    return <TRPCClientErrorDisplay error={opportunities.error} />;
  }

  return (
    <OpportunitiesTable
      organizationId={organizationId}
      contactId={contactId}
      data={opportunities.data ?? []}
    />
  );
}

type OpportunitiesList =
  InferTransformedProcedureOutput<"opportunities.listByContact">;

interface OpportunitiesTableProps {
  organizationId: OrganizationId;
  contactId: ContactId;
  data: OpportunitiesList;
}

function OpportunitiesTable({
  organizationId,
  contactId,
  data,
}: OpportunitiesTableProps) {
  const navigate = useNavigate();

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<OpportunitiesList[number]>();
    return [
      columnHelper.accessor("id", {
        header: "Opportunity",
        cell: (ctx) => (
          <Link
            className="font-medium text-slate-900 underline transition-colors hover:text-slate-500"
            to="/admin/organizations/$organizationId/opportunities/$opportunityId/contacts/$contactId"
            params={{
              organizationId,
              opportunityId: ctx.getValue(),
              contactId,
            }}
          >
            {ctx.getValue()}
          </Link>
        ),
      }),
      columnHelper.accessor("latestEvaluationScore", {
        header: "Latest Score",
        cell: (ctx) => {
          const latestEvaluationScore = ctx.getValue();
          return `${Math.round(latestEvaluationScore * 100)}%`;
        },
      }),
      columnHelper.accessor("latestActivityAt", {
        header: "Last Activity",
        cell: (ctx) => formatDate(ctx.getValue()),
      }),
    ];
  }, [organizationId, contactId]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    initialState: {
      sorting: [{ id: "latestActivityAt", desc: true }],
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <DataTable
      title="Opportunities"
      emptyMessage="No opportunities found"
      table={table}
      onRowClick={(row) => {
        void navigate({
          to: "/admin/organizations/$organizationId/opportunities/$opportunityId/contacts/$contactId",
          params: {
            organizationId,
            opportunityId: row.original.id,
            contactId,
          },
        });
      }}
    />
  );
}
